import type * as React from 'react';

import { useHoverWithDebounce } from '../../hooks';
import { cn } from '../../lib/utils';
import {
  ChangeIndicator,
  type ChangeIndicatorDirection,
  Donut,
  WidgetBreakdownPopover,
  type WidgetBreakdownItem,
} from '../../molecules';

/**
 * Pixel width of the proportion-bar cell in Figma 5:16173. The bar inside the
 * cell is rendered as `(percent / 100) × MAX_BAR_WIDTH`, capped to the cell
 * width so the bar can never overflow visually even at 100 %.
 */
const MAX_BAR_WIDTH = 24;

/**
 * Finite-aware clamp for 0-100 percent inputs. NaN / ±Infinity collapse to 0;
 * values outside the range clamp to the nearest endpoint. Prevents the silent
 * visual-incoherence class (bar overflows cell / zero-width / `NaNpx` ignored).
 */
function clampPercent(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(Math.max(value, 0), 100);
}

export interface IdentityAccessBodyProps {
  total: number;
  change: number;
  changeDirection: ChangeIndicatorDirection;
  activeCount: number;
  /** Also the donut's own percent: it is the one share on this card that moves with the window. */
  activePercent: number;
  nonActiveCount: number;
  nonActivePercent: number;
  /** Empty suppresses the hover popover: a card with a title row and no items is a dud. */
  breakdown: readonly WidgetBreakdownItem[];
  /** Caption under the primary number ("Human Identities" / "Non Human Identities"). */
  caption: string;
  /** Title row inside the popover ("Total Human Identities" / "Total NHI"). */
  popoverTitle: string;
  /** Optional category label inside the popover ("Department" / "Identities"). */
  popoverCategory?: string;
  /**
   * Label rendered inside the donut ring under the percent (e.g. "Data\nAccess").
   * Embed `\n` to split across two lines per Figma.
   */
  donutCenterLabel: string;
  /** Legend label for the active count row (e.g. "Active"). */
  activeLegendLabel: string;
  /** Legend label for the non-active count row (e.g. "Non-active"). */
  nonActiveLegendLabel: string;
  /** Formatter applied to every number on the card and inside popover. */
  formatNumber: (value: number) => string;
  /** Storybook-only override that bypasses the hover debounce. */
  forceHoverOpen?: boolean;
  className?: string;
}

/**
 * Populated body for the Human Identities / Non Human Identities widget cards.
 *
 * Layout matches Figma node 5:16173 — donut on the left with a two-line
 * `"Data\nAccess"` center label, a 16-px gap to the right column, then the
 * total + ChangeIndicator + caption stacked above the active / non-active
 * legend rows (24-px gap between the two clusters, 9-px gap between the legend
 * rows). Each legend row is a 24-px bar cell with a proportional fill, a
 * 14-px gap, and a single text label `"<percent>% <state> (<count>)"`.
 *
 * Donut binding: the donut's `percent` prop is bound to `activePercent`. It was
 * bound to a file-share metric (design §5 Decision #5 / PRD story 41) that reads
 * 100 % on every human population and 0 % on every non-human one, because humans
 * touch every file anyone touches. It could not move with the window filter and
 * could not differ from its complement.
 *
 * `forceHoverOpen` is a Storybook-only override that bypasses the debounce so
 * designers can inspect the open popover without hovering.
 */
export const IdentityAccessBody: React.FC<IdentityAccessBodyProps> = ({
  total,
  change,
  changeDirection,
  activeCount,
  activePercent,
  nonActiveCount,
  nonActivePercent,
  breakdown,
  caption,
  popoverTitle,
  popoverCategory,
  donutCenterLabel,
  activeLegendLabel,
  nonActiveLegendLabel,
  formatNumber,
  forceHoverOpen,
  className,
}) => {
  const hover = useHoverWithDebounce();
  const showPopover = (forceHoverOpen === true || hover.isHovered) && breakdown.length > 0;

  const clampedActivePercent = clampPercent(activePercent);
  const clampedNonActivePercent = clampPercent(nonActivePercent);
  const activeBarWidth = (clampedActivePercent / 100) * MAX_BAR_WIDTH;
  const nonActiveBarWidth = (clampedNonActivePercent / 100) * MAX_BAR_WIDTH;

  return (
    <div className={cn('relative flex items-center gap-4 font-outfit text-dawn-black', className)}>
      {/* Donut column — center label is provided by the consumer (typically a
          two-line string with an embedded `\n`); percent is the active share. */}
      <Donut percent={activePercent} centerLabel={donutCenterLabel} />

      {/* Right column: total + caption (top), then legend rows (bottom). */}
      <div className="flex flex-1 flex-col gap-6">
        {/* Hoverable total cluster — single debounced popover trigger.
            `w-fit` so the popover (anchored at `left-full`) sits flush with the
            right edge of the cluster's content rather than being pushed to the
            far edge of the right column's `flex-1` width. */}
        <div
          data-testid="identity-total-row"
          className="relative flex w-fit flex-col gap-2"
          onMouseEnter={hover.onMouseEnter}
          onMouseLeave={hover.onMouseLeave}
        >
          {/* `h-[17px]` locks the number row to Figma node 5:22963 / 5:23008's
              17 px frame even though the 24 px text overflows visibly — matches
              the FilesCoveredBody trick so the cluster collapses to the 34 px
              total Figma reserves. */}
          <div className="flex h-[17px] items-center gap-2">
            <span className="text-[24px] font-medium leading-[1.2] tabular-nums">
              {formatNumber(total)}
            </span>
            <ChangeIndicator value={change} direction={changeDirection} />
          </div>
          {/* `leading-none` trims the half-leading that would otherwise pad
              the caption row to ~17 px, matching Figma's 9 px caption frame. */}
          <span className="text-[13px] font-normal leading-none">{caption}</span>

          {/* `pl-2`, not `ml-2`: the gap has to stay inside this subtree or
              crossing it fires the row's onMouseLeave and shuts the card. */}
          {showPopover ? (
            <div data-testid="widget-popover" className="absolute left-full top-0 z-30 pl-2">
              <WidgetBreakdownPopover
                title={popoverTitle}
                total={formatNumber(total)}
                categoryLabel={popoverCategory}
                items={breakdown}
                formatCount={formatNumber}
              />
            </div>
          ) : null}
        </div>

        {/* Legend rows: 9-px vertical gap, 14-px gap between bar cell and text. */}
        <div className="flex flex-col gap-[9px]">
          <div data-testid="identity-active-row" className="flex items-center gap-3.5">
            <span className="flex h-2 w-6 shrink-0 items-center" aria-hidden="true">
              <span
                data-testid="identity-active-bar"
                className="block h-2 rounded-sm bg-[#13D67E]"
                style={{ width: `${activeBarWidth}px` }}
              />
            </span>
            <span className="text-[13px] font-normal leading-none tabular-nums">
              {clampedActivePercent}% {activeLegendLabel} ({formatNumber(activeCount)})
            </span>
          </div>

          <div data-testid="identity-non-active-row" className="flex items-center gap-3.5">
            <span className="flex h-2 w-6 shrink-0 items-center" aria-hidden="true">
              <span
                data-testid="identity-non-active-bar"
                className="block h-2 rounded-sm bg-error"
                style={{ width: `${nonActiveBarWidth}px` }}
              />
            </span>
            <span className="text-[13px] font-normal leading-none tabular-nums">
              {clampedNonActivePercent}% {nonActiveLegendLabel} ({formatNumber(nonActiveCount)})
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

IdentityAccessBody.displayName = 'IdentityAccessBody';
